// AllocationPanel.tsx — platform-admin allocation management panel
// Visible only when user has platform-admin role. Renders below TileGrid in AppShell.
// Calls /api/broker/v1/registry/* directly using the Keycloak bearer token.
import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';

interface ServiceSummary {
  id: string;
  name: string;
  state: string;
  service_class: string;
  visibility: string;
}

interface Allocation {
  allocation_id: string;
  vertical_broker_id: string;
  channel: string;
  state: string;
  allocated_at: string;
  allocated_by: string;
  plan_subset: string[] | null;
}

const API = '/api/broker/v1/registry';

export default function AllocationPanel() {
  const { keycloak } = useAuth();
  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formBroker, setFormBroker] = useState('');
  const [formChannel, setFormChannel] = useState('stable');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');

  function authFetch(path: string, init?: RequestInit): Promise<Response> {
    const token = keycloak?.token;
    if (!token) return Promise.reject(new Error('Not authenticated'));
    return fetch(path, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  }

  // Load published + validated services on mount
  useEffect(() => {
    setLoading(true);
    authFetch(`${API}/services?state=published`)
      .then((r) => r.json())
      .then((d: { services: ServiceSummary[] }) => setServices(d.services ?? []))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload allocations when selection changes
  useEffect(() => {
    if (!selectedId) { setAllocations([]); return; }
    authFetch(`${API}/services/${selectedId}/allocations`)
      .then((r) => r.json())
      .then((d: { allocations: Allocation[] }) => setAllocations(d.allocations ?? []))
      .catch((e: Error) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  async function handleAllocate(e: FormEvent) {
    e.preventDefault();
    if (!selectedId || !formBroker) return;
    setSubmitting(true); setMsg(''); setError('');
    try {
      const resp = await authFetch(`${API}/services/${selectedId}/allocations`, {
        method: 'POST',
        body: JSON.stringify({ vertical_broker_id: formBroker, channel: formChannel }),
      });
      const body = (await resp.json()) as { error?: string; allocation_id?: string };
      if (!resp.ok) throw new Error(body.error ?? resp.statusText);
      setMsg(`Allocated to ${formBroker} (${formChannel}). ID: ${body.allocation_id ?? ''}`);
      setFormBroker('');
      const r2 = await authFetch(`${API}/services/${selectedId}/allocations`);
      const d2 = (await r2.json()) as { allocations: Allocation[] };
      setAllocations(d2.allocations ?? []);
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(allocationId: string) {
    if (!selectedId) return;
    setMsg(''); setError('');
    try {
      const resp = await authFetch(
        `${API}/services/${selectedId}/allocations/${allocationId}/remove`,
        { method: 'POST' },
      );
      if (!resp.ok) {
        const b = (await resp.json()) as { error: string };
        throw new Error(b.error ?? resp.statusText);
      }
      setMsg('Allocation removed.');
      const r2 = await authFetch(`${API}/services/${selectedId}/allocations`);
      const d2 = (await r2.json()) as { allocations: Allocation[] };
      setAllocations(d2.allocations ?? []);
    } catch (err: unknown) {
      setError((err as Error).message);
    }
  }

  const active = allocations.filter((a) => a.state === 'active');
  const removed = allocations.filter((a) => a.state === 'removed');

  return (
    <section className="allocation-panel">
      <h2 className="allocation-panel-title">
        Service Allocations{' '}
        <span className="tile-badge tile-badge-private">ADMIN</span>
      </h2>

      {loading && <p className="allocation-status">Loading services…</p>}
      {error && <p className="allocation-error">{error}</p>}
      {msg && <p className="allocation-msg">{msg}</p>}

      <div className="allocation-row">
        <label htmlFor="alloc-service">Service</label>
        <select
          id="alloc-service"
          value={selectedId}
          onChange={(e) => { setSelectedId(e.target.value); setMsg(''); setError(''); }}
        >
          <option value="">— select published service —</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} [{s.id}]
            </option>
          ))}
        </select>
      </div>

      {selectedId && (
        <>
          <div className="allocation-current">
            <h3>Active allocations ({active.length})</h3>
            {active.length === 0 ? (
              <p className="allocation-empty">No active allocations.</p>
            ) : (
              <table className="allocation-table">
                <thead>
                  <tr>
                    <th>Vertical broker</th>
                    <th>Channel</th>
                    <th>Since</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((a) => (
                    <tr key={a.allocation_id}>
                      <td>{a.vertical_broker_id}</td>
                      <td>{a.channel}</td>
                      <td>{new Date(a.allocated_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="allocation-btn-remove"
                          onClick={() => void handleRemove(a.allocation_id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {removed.length > 0 && (
              <p className="allocation-removed-note">{removed.length} removed allocation(s) not shown.</p>
            )}
          </div>

          <form className="allocation-form" onSubmit={(e) => void handleAllocate(e)}>
            <h3>New allocation</h3>
            <div className="allocation-row">
              <label htmlFor="alloc-broker">Vertical broker ID</label>
              <input
                id="alloc-broker"
                type="text"
                placeholder="e.g. deal-broker"
                value={formBroker}
                onChange={(e) => setFormBroker(e.target.value)}
                required
              />
            </div>
            <div className="allocation-row">
              <label htmlFor="alloc-channel">Channel</label>
              <select
                id="alloc-channel"
                value={formChannel}
                onChange={(e) => setFormChannel(e.target.value)}
              >
                <option value="stable">stable</option>
                <option value="beta">beta</option>
                <option value="edge">edge</option>
              </select>
            </div>
            <button
              type="submit"
              className="allocation-btn-submit"
              disabled={submitting || !formBroker}
            >
              {submitting ? 'Allocating…' : 'Allocate'}
            </button>
          </form>
        </>
      )}
    </section>
  );
}
